import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { IEventoGenerico, IReporteBase, IUsuario } from 'modelos/src';
import {
  HelperService,
  Modulo,
} from '../../../../../auxiliares/servicios/helper.service';

export interface IContadorTipo {
  label: string;
  icon: string;
  count: number;
  key: string; // clave usada en el mapa / filtro
}

@Component({
  selector: 'app-flotante-servicio-tecnico',
  templateUrl: './flotante-servicio-tecnico.component.html',
  styleUrl: './flotante-servicio-tecnico.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlotanteServicioTecnicoComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private helper = inject(HelperService);

  /** Vehículos como pill se muestra solo si el usuario tiene el módulo Vehículos. */
  get mostrarVehiculos(): boolean {
    return this.helper.tieneModulo('Vehículos');
  }

  private _eventos: IEventoGenerico[] = [];

  @Input() set eventos(value: IEventoGenerico[]) {
    this._eventos = value;
    this.actualizarContadores();
    this.cdr.markForCheck();
  }
  get eventos(): IEventoGenerico[] {
    return this._eventos;
  }

  @Input() vehiculosTecnicos: IReporteBase<'Tracker 4G'>[] = [];
  @Input() vehiculosPendientesCount = 0;
  @Input() tecnicos: IUsuario[] = [];

  @Output() tecnicoChange = new EventEmitter<IUsuario | null>();
  @Output() tiposActivosChange = new EventEmitter<Set<string>>();

  public expansionPanelExpanded = true;
  public contadoresPorTipo: IContadorTipo[] = [];
  public tiposActivos = new Set<string>([
    'alarmas',
    'luminarias',
    'trackers',
    'sirenas',
    'vehiculos',
    'tecnicos',
  ]);

  // Búsqueda de técnico
  public searchTecnico = '';
  public tecnicoSeleccionado: IUsuario | null = null;

  private readonly TIPOS: {
    tipoEvento: string;
    label: string;
    icon: string;
    key: string;
    modulos: Modulo[]; // módulos que habilitan mostrar esta pill
  }[] = [
    {
      tipoEvento: 'Evento Técnico Alarma',
      label: 'Alarmas',
      icon: 'doorbell',
      key: 'alarmas',
      modulos: ['Alarmas'],
    },
    {
      tipoEvento: 'Evento Técnico Luminaria',
      label: 'Luminarias',
      icon: 'lightbulb',
      key: 'luminarias',
      modulos: ['Luminarias'],
    },
    {
      tipoEvento: 'Evento Técnico Tracker',
      label: 'Trackers',
      icon: 'gps_fixed',
      key: 'trackers',
      modulos: ['Vehículos', 'Activos', 'Colectivos'],
    },
    {
      tipoEvento: 'Evento Técnico Sirena',
      label: 'Sirenas',
      icon: 'campaign',
      key: 'sirenas',
      modulos: ['Sirenas'],
    },
  ];

  get totalEventos(): number {
    return this._eventos.length;
  }

  get tecnicosFiltrados(): IUsuario[] {
    if (!this.searchTecnico.trim()) return this.tecnicos;
    const termino = this.searchTecnico.toLowerCase();
    return this.tecnicos.filter(
      (t) =>
        t.datosPersonales?.nombre?.toLowerCase().includes(termino) ||
        t.usuario?.toLowerCase().includes(termino),
    );
  }

  public nombreTecnico(t: IUsuario): string {
    return t.datosPersonales?.nombre || t.usuario || t._id || '';
  }

  public vehiculoDeTecnico(t: IUsuario): string | null {
    const v = this.vehiculosTecnicos.find(
      (v) => (v.activo as any)?.vehiculo?.idChofer === t._id,
    );
    return (v?.activo as any)?.identificacion || null;
  }

  public toggleTipo(key: string): void {
    if (this.tiposActivos.has(key)) {
      this.tiposActivos.delete(key);
    } else {
      this.tiposActivos.add(key);
    }
    this.tiposActivos = new Set(this.tiposActivos); // nueva referencia para detección
    this.tiposActivosChange.emit(this.tiposActivos);
    this.cdr.markForCheck();
  }

  public displayTecnico = (t: IUsuario | string): string => {
    if (!t) return '';
    if (typeof t === 'string') return t;
    return this.nombreTecnico(t);
  };

  public onTecnicoSelected(event: MatAutocompleteSelectedEvent): void {
    this.seleccionarTecnico(event.option.value);
  }

  public seleccionarTecnico(tecnico: IUsuario): void {
    this.tecnicoSeleccionado = tecnico;
    this.searchTecnico = this.nombreTecnico(tecnico);
    this.tecnicoChange.emit(tecnico);
    this.cdr.markForCheck();
  }

  public limpiarTecnico(): void {
    this.tecnicoSeleccionado = null;
    this.searchTecnico = '';
    this.tecnicoChange.emit(null);
    this.cdr.markForCheck();
  }

  private actualizarContadores(): void {
    this.contadoresPorTipo = this.TIPOS.filter((t) =>
      t.modulos.some((m) => this.helper.tieneModulo(m)),
    ).map((t) => ({
      label: t.label,
      icon: t.icon,
      key: t.key,
      count: this._eventos.filter((e) => (e as any).tipoEvento === t.tipoEvento)
        .length,
    }));
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const wasMobile = !this.expansionPanelExpanded;
    const isMobileNow = event.target.innerWidth <= 768;
    if (wasMobile !== isMobileNow) {
      this.expansionPanelExpanded = !isMobileNow;
    }
  }

  ngOnInit(): void {
    this.expansionPanelExpanded = window.innerWidth > 768;
  }
}
